import { useState, useCallback } from "react";
import { CloudUpload } from "lucide-react";
import { twMerge } from "tailwind-merge";
import {
  FileUploaderBase,
  type FileUploaderError,
  fileToBase64,
} from "@core/features/uploadFile";
import { Button } from "../Button";
import { Type } from "../Type";
import { Label } from "../Label";
import {
  dropzoneBaseStyles,
  dropzoneDragActiveStyles,
  dropzoneIconStyles,
  dropzoneHintStyles,
  dropzoneDisabledStyles,
  dropzoneErrorStyles,
} from "../Dropzone";
import type { UploadedFile } from "../GenericUploadInput";

const MAX_FILE_SIZE = 20 * 1024 * 1024;

const ALLOWED_IMAGE_TYPES: Record<string, boolean> = {
  "image/jpeg": true,
  "image/png": true,
  "image/gif": true,
  "image/svg+xml": true,
  "image/webp": true,
};

export type Props = {
  /** Field name for identification */
  name: string;
  /** Currently uploaded photo value */
  value?: UploadedFile | null;
  /** Change handler - receives uploaded photo data */
  onChange?: (value: UploadedFile | null) => void;
  /** Blur handler */
  onBlur?: () => void;
  /** Image alt text (used for img alt attribute) */
  altText?: string;
  /** Size of the circular image in pixels */
  size?: number;
  /** Whether the field has an error */
  error?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Test ID */
  "data-testid"?: string;
};

/**
 * Resizes an image file to fit within max dimensions while maintaining aspect ratio
 */
async function resizeImage(
  file: File,
  maxWidth = 1200,
  maxHeight = 1200
): Promise<File> {
  if (!file.type.startsWith("image/") || file.size <= MAX_FILE_SIZE) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(maxWidth / width, maxHeight / height, 1);
        width *= scale;
        height *= scale;

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("Canvas context unavailable"));
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              return reject(new Error("Failed to convert canvas to blob"));
            }
            resolve(
              new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              })
            );
          },
          file.type,
          0.85
        );
      };
      img.onerror = () => reject(new Error("Image load failed"));
    };
    reader.onerror = () => reject(new Error("File read failed"));
  });
}

/**
 * Photo upload component with circular preview and drag-and-drop support.
 * Built on FileUploaderBase for consistent UX with ImageField.
 *
 * Features:
 * - Automatic image resizing
 * - Circular preview with border
 * - Drag-and-drop support
 * - Alt text input (optional)
 * - Supports JPEG, PNG, GIF, SVG, WebP
 *
 * @example
 * // Standalone usage
 * <PhotoUploadInput
 *   name="avatar"
 *   value={photo}
 *   onChange={setPhoto}
 * />
 *
 * @example
 * // With FormField (includes label, alt text input, and error display)
 * import { FormField } from "@themes/pollinator/ui/FormField";
 * import { Form } from "@themes/pollinator/ui/Form";
 *
 * <Form form={form}>
 *   <FormField
 *     name="avatar"
 *     label="Photo"
 *     component="PhotoUploadInput"
 *     requireAltText
 *   />
 * </Form>
 */
export function PhotoUploadInput({
  name,
  value,
  onChange,
  onBlur: _onBlur,
  altText = "",
  size = 120,
  error = false,
  disabled = false,
  className,
  "data-testid": testId = "photo-upload",
}: Props) {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const hasImage = !!value?.file;

  // Handle file selection from FileUploaderBase
  const handleFileChange = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file) return;

      // Validate file type
      if (!ALLOWED_IMAGE_TYPES[file.type]) {
        setUploadError(`File type ${file.type} is not supported.`);
        return;
      }

      setUploadError(null);
      setIsUploading(true);
      setProgress(0);

      try {
        // Simulate progress for resize/conversion
        setProgress(30);
        const resized = await resizeImage(file);

        if (resized.size > MAX_FILE_SIZE) {
          setUploadError("File too large.");
          setIsUploading(false);
          return;
        }

        setProgress(60);
        const base64 = await fileToBase64(resized);
        setProgress(100);

        const uploadedFile: UploadedFile = {
          file: base64,
          name: resized.name,
          size: resized.size,
          type: resized.type,
          original: resized,
          altText: value?.altText,
        };

        onChange?.(uploadedFile);
      } catch (err: any) {
        setUploadError(err.message || "Upload failed");
      } finally {
        setIsUploading(false);
        setProgress(0);
      }
    },
    [onChange, value?.altText]
  );

  // Handle errors from FileUploaderBase
  const handleError = useCallback((err: FileUploaderError) => {
    setUploadError(err.message);
  }, []);

  // Calculate ring progress for uploading state
  const circumference = 2 * Math.PI * (size / 2 - 4);
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  // Build accept string from allowedFileTypes
  const acceptString = Object.keys(ALLOWED_IMAGE_TYPES).join(",");

  return (
    <div
      className={twMerge(
        "flex flex-col items-start gap-4 w-full",
        disabled && "opacity-50 pointer-events-none",
        className
      )}
      data-testid={testId}
    >
      <FileUploaderBase
        name={`${name}-file`}
        accept={acceptString}
        disabled={disabled || isUploading}
        onChange={handleFileChange}
        onError={handleError}
        aria-label={hasImage ? "Replace photo" : "Upload photo"}
        data-testid={`${testId}-uploader`}
        className="w-full"
        renderDropzone={({
          isDragActive,
          disabled: isDropzoneDisabled,
          onClick,
          onDragOver,
          onDragLeave,
          onDrop,
        }) => (
          <div className="flex flex-col items-start gap-2 w-full">
            {/* Empty state - FileUploader design */}
            {!hasImage && !isUploading && (
              <div
                onClick={isDropzoneDisabled ? undefined : onClick}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                role="button"
                tabIndex={isDropzoneDisabled ? -1 : 0}
                onKeyDown={(e) => {
                  if (
                    !isDropzoneDisabled &&
                    (e.key === "Enter" || e.key === " ")
                  ) {
                    e.preventDefault();
                    onClick();
                  }
                }}
                className={twMerge(
                  dropzoneBaseStyles,
                  "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2",
                  isDragActive && dropzoneDragActiveStyles,
                  error && dropzoneErrorStyles,
                  isDropzoneDisabled && dropzoneDisabledStyles
                )}
                aria-disabled={isDropzoneDisabled}
                data-testid={`${testId}-button`}
              >
                <CloudUpload className={dropzoneIconStyles} />
                <Label className="cursor-pointer">
                  Click to upload or drag and drop
                </Label>
                <Type variant="caption" className={dropzoneHintStyles}>
                  PNG, JPG, GIF, SVG, WebP
                </Type>
              </div>
            )}

            {/* Uploading state - circular progress */}
            {isUploading && (
              <div
                className="relative rounded-full overflow-hidden flex items-center justify-center"
                style={{ width: size, height: size }}
                data-testid={`${testId}-button`}
              >
                <div className="relative flex items-center justify-center">
                  {/* Progress ring */}
                  <svg
                    className="absolute transform -rotate-90"
                    width={size}
                    height={size}
                  >
                    {/* Background circle */}
                    <circle
                      cx={size / 2}
                      cy={size / 2}
                      r={size / 2 - 4}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4"
                      className="text-brand-200"
                    />
                    {/* Progress circle */}
                    <circle
                      cx={size / 2}
                      cy={size / 2}
                      r={size / 2 - 4}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                      className="text-brand-600 transition-all duration-200"
                    />
                  </svg>
                  <Type variant="h6" className="text-brand-600">
                    {progress}%
                  </Type>
                </div>
              </div>
            )}

            {/* Uploaded state - circular image */}
            {hasImage && !isUploading && (
              <>
                <div
                  onClick={isDropzoneDisabled ? undefined : onClick}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  role="button"
                  tabIndex={isDropzoneDisabled ? -1 : 0}
                  onKeyDown={(e) => {
                    if (
                      !isDropzoneDisabled &&
                      (e.key === "Enter" || e.key === " ")
                    ) {
                      e.preventDefault();
                      onClick();
                    }
                  }}
                  className={twMerge(
                    "relative rounded-full overflow-hidden flex items-center justify-center cursor-pointer transition-all duration-200",
                    "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2",
                    "border-2 border-brand-200 hover:border-brand-400",
                    isDragActive && "border-brand-600 bg-brand-50",
                    error && dropzoneErrorStyles,
                    isDropzoneDisabled && "cursor-not-allowed"
                  )}
                  style={{ width: size, height: size }}
                  aria-disabled={isDropzoneDisabled}
                  data-testid={`${testId}-button`}
                >
                  <img
                    src={value?.file}
                    alt={altText || "Uploaded photo"}
                    className="w-full h-full object-cover"
                    data-testid={`${testId}-preview`}
                  />
                </div>

                {/* Replace button */}
                <Button
                  variant="secondary"
                  onClick={onClick}
                  disabled={isDropzoneDisabled}
                  data-testid={`${testId}-replace-button`}
                >
                  Replace
                </Button>
              </>
            )}
          </div>
        )}
      />

      {/* Error message */}
      {uploadError && (
        <Type
          variant="caption"
          className="text-error"
          role="alert"
          data-testid={`${testId}-error`}
        >
          {uploadError}
        </Type>
      )}
    </div>
  );
}

export default PhotoUploadInput;
